import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { Alert, Button, Divider, Flex, Input, notification } from "antd";

import { api } from "../../api";
import AuthCard from "../../components/auth/AuthCard";
import { GoogleSignInButton } from "../../components/auth/GoogleSignInButton";
import { YandexSignInButton } from "../../components/auth/YandexSignInButton";
import { YANDEX_COUNTRIES } from "../../config";
import { signupStore } from "../../stores/signupStore";
import { usePageTitle } from "../../utils/usePageTitle";
import { usePendingAction } from "../../utils/usePendingAction";
import { validateEmail } from "../../utils/validateEmail";

import "./SignupPage.css";

export const SignupPage = () => {
  const { t, i18n } = useTranslation();
  usePageTitle(t("signup.title"));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState(
    searchParams.get("email") ??
      (location.state as { email?: string })?.email ??
      "",
  );
  const [emailExists, setEmailExists] = useState(false);
  const { pending: loading, run: runContinue } = usePendingAction();
  const [emailError, setEmailError] = useState("");
  const [geoResolved, setGeoResolved] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);
  const [showYandex, setShowYandex] = useState(false);

  useEffect(() => {
    if (!searchParams.has("email")) return;

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("email");
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    api.countries
      .getCurrentCountry(i18n.language)
      .then((current) => {
        setShowGoogle(current.code !== "RU");
        setShowYandex(
          current.code !== null && YANDEX_COUNTRIES.has(current.code),
        );
      })
      .catch(() => {
        setShowGoogle(true);
        setShowYandex(false);
      })
      .finally(() => {
        setGeoResolved(true);
      });
  }, [i18n.language]);

  async function handleContinue() {
    if (!email || !validateEmail(email)) {
      setEmailError(t("errors.shared.invalid_email"));
      return;
    }
    await runContinue(async () => {
      setEmailError("");
      setEmailExists(false);
      try {
        const result = await api.auth.registerEmail(email);
        if (result.ok) {
          signupStore.set(email, result.token);
          signupStore.setNext(searchParams.get("next") ?? "");
          navigate("/signup/email-verification");
        } else if (result.code === "user_already_exists") {
          setEmailExists(true);
        }
      } catch {
        notification.error({
          message: t("errors.shared.common_error"),
          placement: "topRight",
        });
      }
    });
  }

  return (
    <AuthCard>
      <h1>{t("signup.title")}</h1>
      <p>{t("signup.description")}</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Input
          type="text"
          name="email"
          placeholder={t("app.yourEmail")}
          allowClear
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError("");
            if (emailExists) setEmailExists(false);
          }}
          onPressEnter={handleContinue}
          autoComplete="email"
          size="large"
          status={emailError ? "error" : undefined}
        />
        {emailError && <div className="email-error">{emailError}</div>}

        {emailExists && (
          <Alert
            type="error"
            style={{ marginTop: 16 }}
            className="signup-alert"
            classNames={{ description: "signup-alert__description" }}
            description={
              <Trans
                i18nKey="signup.emailAlreadyExists"
                components={{ lnk: <Link to="/login/1" state={{ email }} /> }}
              />
            }
          />
        )}

        <Flex gap={12} align="center" wrap="wrap" style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            loading={loading}
            onClick={handleContinue}
            className="signup-actions__continue"
          >
            {t("app.continue")}
          </Button>
          <Button
            size="large"
            onClick={() => {
              const next = searchParams.get("next");
              navigate(
                `/login/1${next ? `?next=${encodeURIComponent(next)}` : ""}`,
                { state: { email } },
              );
            }}
            className="signup-actions__signin"
          >
            {t("signin.title")}
          </Button>
        </Flex>
      </form>

      {geoResolved && (
        <>
          <Divider plain className="divider-or">
            {t("app.or")}
          </Divider>
          <Flex vertical gap={8}>
            {showGoogle && <GoogleSignInButton />}
            {showYandex && <YandexSignInButton />}
          </Flex>
        </>
      )}

      <div className="signup-mobile-links">
        <Link
          to={`/login/1${searchParams.get("next") ? `?next=${encodeURIComponent(searchParams.get("next")!)}` : ""}`}
          state={{ email }}
        >
          {t("signin.title")}
        </Link>
      </div>
    </AuthCard>
  );
};
